// ============================================================
// TANGOALPHA BT — methodology and shortcomings.
//
// The caveats travel with the numbers. Someone who reaches the P&L chart
// without having read METHODOLOGY.md should still be one click from knowing
// what the number does and does not mean.
//
// This is a condensation of METHODOLOGY.md in the sabr_dnn repository; that
// file is the authority and carries the full §9 catalogue.
// ============================================================
import React from 'react';
import { C, F, MONO } from './theme';
import { S } from './ui';
import { BT_ACCENT } from './btShared';

const wrap = { padding: '18px 28px 60px', maxWidth: 980 };
const h1 = { fontSize: F.h1, fontWeight: 700, letterSpacing: '-.02em', margin: '0 0 10px' };
const h2 = {
  fontSize: F.h2, fontWeight: 700, letterSpacing: '-.01em',
  margin: '28px 0 8px', paddingBottom: 5, borderBottom: `1px solid ${C.bd}`,
};
const h3 = {
  fontSize: F.xs, fontWeight: 700, color: BT_ACCENT, textTransform: 'uppercase',
  letterSpacing: '.1em', margin: '18px 0 6px',
};
const p = { fontSize: F.md, color: C.txD, lineHeight: 1.65, margin: '0 0 10px' };
const ul = { margin: '0 0 10px', paddingLeft: 20 };
const li = { fontSize: F.md, color: C.txD, lineHeight: 1.65, marginBottom: 7 };
const st = { color: C.tx, fontWeight: 600 };

function K({ children }) {
  return (
    <span style={{ fontFamily: MONO, fontSize: F.sm, color: C.tx,
                   background: C.bgC, border: `1px solid ${C.bd}`,
                   borderRadius: 3, padding: '1px 5px' }}>{children}</span>
  );
}

export default function BTMethod() {
  return (
    <div style={wrap}>
      <div style={h1}>Methodology and shortcomings</div>
      <p style={p}>
        This is a <span style={st}>relative-value research tool</span>. It answers questions of the
        form &ldquo;what would selling this straddle and delta-hedging it have earned, gross of
        financing, at a stated bid/offer?&rdquo; It is not a booking system, not a front-office risk
        system, and not a term-structure model — it takes the calibrated surface as given.
      </p>
      <p style={p}>
        Coverage: <span style={st}>2017-01-03 → 2021-09-23</span>, 1,145 business dates, expiries
        {' '}<K>1M 2M 3M 6M 9M 1Y</K> × tenors <K>2Y 5Y 10Y 30Y</K>. Everything runs in your browser
        from a 0.56 MB file of calibrated parameters; the 164 MB raw vol cube is never needed, because
        every implied vol is regenerated from the parameters on demand.
      </p>

      <div style={h2}>Conventions</div>
      <ul style={ul}>
        <li style={li}>
          Rates and strikes are absolute decimals and <span style={st}>may be negative</span> — EUR
          was through most of the sample.
        </li>
        <li style={li}>Volatilities are normal (Bachelier), absolute: 0.0050 = 50 bp/yr.</li>
        <li style={li}>
          <K>notional</K> is signed: positive is long (premium paid), negative short.
        </li>
        <li style={li}>
          <K>annuity</K> is the market PV01 of the underlying swap, taken from the data —{' '}
          <em>not</em> the swap tenor in years, which is a 21% error for 30Y (24.7 against 30.0) and,
          being constant, deletes the annuity&apos;s own P&amp;L.
        </li>
        <li style={li}>
          P&amp;L is in <span style={st}>annuity units</span>: PV per unit notional. Multiply by your
          notional for currency.
        </li>
        <li style={li}>
          Delta is <K>dPV/dF</K>; vega is per unit normal vol; theta is per <em>year</em> and negative
          for a long option.
        </li>
      </ul>

      <div style={h3}>Two kinds of time</div>
      <p style={p}>
        Each pillar carries both the <span style={st}>actual ACT/365 time</span> to its adjusted
        expiry — the term axis, what variance accumulates over — and the{' '}
        <span style={st}>nominal year fraction the smile was calibrated at</span>, which is what goes
        into the SABR formula. Using either for the other&apos;s job is a bug, and both bugs were
        present in earlier versions of this engine.
      </p>

      <div style={h2}>What the engine guarantees</div>
      <p style={p}>
        Every price — opening fill, daily mark, closing fill, expiry settlement, hedge mark — comes
        from one function. A fill and the mark an instant later therefore differ by exactly the
        half-spread. Three checks run on <em>every</em> backtest, and their verdict is the badge at
        the top of the results:
      </p>
      <ol style={ul}>
        <li style={li}><span style={st}>NAV = cash + marks</span>, continuously, across every event.</li>
        <li style={li}><span style={st}>Cash matches the trade log</span>, rebuilt independently.</li>
        <li style={li}>
          <span style={st}>Trading P&amp;L equals minus the spread</span>, exactly. If fills and marks
          agree, the entire P&amp;L impact of trading is the spread crossed.
        </li>
      </ol>
      <p style={p}>
        Measured over 1,145 dates: NAV error 7e-18, cash error 4e-17, largest unexplained daily move
        1.5e-17, residual delta after hedging 9e-16. A result that fails these is not a result.
      </p>

      <div style={h2}>Shortcomings</div>
      <p style={p}>
        The full catalogue is §9 of <K>METHODOLOGY.md</K>. The ones that most change how a number
        should be read:
      </p>

      <div style={h3}>Risk aggregation</div>
      <ul style={ul}>
        <li style={li}>
          <span style={st}>Delta is summed across expiries as if forwards moved together.</span>{' '}
          Adding a 3M×10Y delta to a 1Y×10Y delta assumes a parallel shift of the forward surface. A
          single swap cannot hedge both. Fine for a book concentrated in one expiry; wrong for a
          calendar spread.
        </li>
        <li style={li}>
          <span style={st}>Delta holds the annuity fixed</span>, ignoring ∂A/∂F. Consistent with
          hedging in a swap of the same annuity, but a first-order approximation to the true hedge
          ratio.
        </li>
        <li style={li}>
          <span style={st}>No skew or smile risk.</span> Vega is one number per leg — the sensitivity
          to a parallel shift of that leg&apos;s own vol. A trade that is vega-flat but short skew
          looks riskless here.
        </li>
        <li style={li}>
          <span style={st}>Tenors are independent slices.</span> No interpolation across tenor, so
          only the four pillars can be traded, and a 2Y-vs-30Y trade carries no modelled correlation.
        </li>
      </ul>

      <div style={h3}>Surface</div>
      <ul style={ul}>
        <li style={li}>
          <span style={st}>The longest expiry is 1Y.</span> An option opened at that pillar sits at
          the edge of the surface on day one; nothing longer can be traded at all.
        </li>
        <li style={li}>
          Forward and annuity are interpolated <span style={st}>linearly in T</span>. A forward swap
          rate is not linear in start date; over the 6M→1Y gap the error is small, on a kinked front
          end it would not be.
        </li>
        <li style={li}>
          The settlement fixing is the forward <span style={st}>extrapolated to T = 0</span>.
          Continuity through expiry is guaranteed, but it is not a quoted spot rate.
        </li>
        <li style={li}>
          Rough SABR carries (H, ρ, η) <span style={st}>per tenor, not per pillar</span>, so it has
          fewer parameters than SABR. Its apparent stability across expiries is partly an artefact of
          that.
        </li>
      </ul>

      <div style={h3}>Discounting and settlement</div>
      <ul style={ul}>
        <li style={li}>
          Prices are <K>annuity × undiscounted Bachelier</K> — exactly right for a{' '}
          <span style={st}>cash-settled</span> swaption, an approximation for a physically-settled one.
        </li>
        <li style={li}>
          <span style={st}>Funding defaults to zero.</span> Premium received sits in cash earning
          nothing unless you set a rate, and the rate is a single constant with no term structure and
          no borrow/lend spread.
        </li>
        <li style={li}>
          The option pays intrinsic on the expiry date itself; a real cash-settled EUR swaption pays
          on the swap&apos;s spot date. No CSA, collateral, CVA or capital.
        </li>
      </ul>

      <div style={h3}>Calibration inputs (inherited, not fixed)</div>
      <ul style={ul}>
        <li style={li}>
          <span style={{ ...st, color: C.ac }}>
            The calibration&apos;s vega weights are on the wrong scale — probably the largest
            remaining error in the project.
          </span>{' '}
          The fit is weighted by a lognormal-vega function fed a <em>normal</em> vol, an ~18× scale
          error, so the ±100 and ±200 bp strikes receive weights of 4e-6 and exactly zero. Since α is
          pinned by the ATM anchor, ρ and ν — the only parameters the wings determine — are fitted to
          essentially nothing. Correcting it would change every historical label, which is a decision
          about the data rather than a bug fix in the backtester.
        </li>
        <li style={li}>
          <span style={st}>ATM fit quality is meaningless</span>: α is solved from the observed ATM
          vol, so ATM matches to machine precision by construction.
        </li>
        <li style={li}>
          Scheduled-event variance (ECB, FOMC) is not applied at mark time, so a strategy trading
          around a known event date is not accounting for it.
        </li>
      </ul>

      <div style={h3}>Execution realism</div>
      <ul style={ul}>
        <li style={li}>
          <span style={st}>Bid/offer is a single constant</span> — no dependence on expiry, tenor,
          moneyness, size, date or stress. Real spreads widened sharply in March 2020, exactly when
          this backtest trades most.
        </li>
        <li style={li}>
          <span style={st}>Infinite liquidity, no market impact.</span> Daily vega-hedging in
          particular would be an implausible amount of real risk transfer.
        </li>
        <li style={li}>
          <span style={st}>Daily close-to-close only.</span> Hedges execute at the same mark used to
          close the day, so gamma P&amp;L understates the cost of hedging a path that whipsawed
          intraday.
        </li>
      </ul>

      <div style={h3}>The sample itself</div>
      <ul style={ul}>
        <li style={li}>
          <span style={st}>2017–2021 is not regime-diverse</span>: negative-rate EUR throughout, ECB
          QE for most of it, one volatility event (March 2020), and a persistent decline in implied
          vol that hands a short-vol strategy a tailwind. The vega P&amp;L on the default strategy is
          largely that decline.
        </li>
        <li style={li}>
          <span style={st}>~12 independent quarterly rolls.</span> A Sharpe computed on twelve
          non-overlapping trades has an enormous standard error. Read the performance statistics as
          descriptive of this path, not as an estimate of an expectation.
        </li>
        <li style={li}>
          <span style={st}>&ldquo;Sharpe&rdquo; has no capital denominator</span> — an option book has
          no natural one. It is mean daily P&amp;L over its standard deviation, annualised, comparable
          across strategies at the same notional and nothing more.
        </li>
      </ul>

      <div style={h3}>This page specifically</div>
      <ul style={ul}>
        <li style={li}>
          The TypeScript engine here is a <span style={st}>second implementation</span> of the Python
          one in <K>sabr_dnn</K>. It is pinned by a golden-fixture parity test — Python generates
          thousands of reference values over the awkward regions, and <K>npm test</K> fails if the two
          disagree — but a parity test covers the cases it samples. The previous JavaScript port of
          this project is the cautionary example: it reproduced six of the seven Python defects
          verbatim <em>and</em> added one of its own, an omitted expiry settlement that meant options
          never expired at all.
        </li>
        <li style={li}>
          <span style={st}>The invariant checks verify internal consistency, not correctness.</span>{' '}
          They prove the ledger balances and every leg is marked. They cannot tell you the vols are
          right. A perfectly self-consistent engine reading a mis-calibrated surface passes all of them.
        </li>
        <li style={li}>
          This tab shares the TangoAlpha Rates data window but <span style={st}>not its pricing
          code</span>. Rates carries its own Bachelier layer against a single as-of surface; BT prices
          1,145 daily calibrated smiles. Two implementations, deliberately — but a drift risk worth
          knowing about.
        </li>
      </ul>

      <div style={h2}>References</div>
      <ul style={ul}>
        <li style={li}>
          Hagan, Lesniewski &amp; Woodward, <em>Probability Distribution in the SABR Model of
          Stochastic Volatility</em>, Wilmott (2017) — the shifted normal-vol expansion.
        </li>
        <li style={li}>
          Fukasawa &amp; Gatheral, <em>A Rough SABR Formula</em> (2022) — the rough-SABR closed form.
        </li>
        <li style={li}>
          Rensi, Rossi &amp; Bianchetti, <em>Learning the Exact SABR Model</em>, arXiv:2510.10343v2 (2025).
        </li>
        <li style={li}>
          Bergomi, <em>Stochastic Volatility Modeling</em> (2016), ch. 2–3 — the P&amp;L attribution
          identity and the theta conventions.
        </li>
      </ul>

      <div style={{ ...S.label, marginTop: 26, color: C.txM }}>
        Full methodology: METHODOLOGY.md in the sabr_dnn repository.
      </div>
    </div>
  );
}
